export type TimestampLike = {
  seconds: number
  nanoseconds: number
  toDate: () => Date
  toMillis: () => number
  toJSON: () => {seconds: number; nanoseconds: number; type: string}
  isEqual: (other: unknown) => boolean
  valueOf: () => string
}

type TimestampObjectLike = Partial<TimestampLike> & {
  _seconds?: number
  _nanoseconds?: number
}

const createTimestampFromMillis = (input: number): TimestampLike => {
  const millis = Number.isFinite(input) ? input : 0
  const seconds = Math.floor(millis / 1000)
  const nanoseconds = Math.max(0, Math.floor((millis - seconds * 1000) * 1_000_000))
  const comparableValue = `${seconds.toString().padStart(12, '0')}.${nanoseconds
    .toString()
    .padStart(9, '0')}`

  return {
    seconds,
    nanoseconds,
    toDate: () => new Date(millis),
    toMillis: () => millis,
    toJSON: () => ({seconds, nanoseconds, type: 'timestamp'}),
    isEqual: (other: unknown) => {
      const normalized = normalizeTimestampLike(other)
      return (
        normalized.seconds === seconds &&
        normalized.nanoseconds === nanoseconds
      )
    },
    valueOf: () => comparableValue
  }
}

export const createTimestampLike = (
  value: Date | number | string = 0
): TimestampLike => {
  if (value instanceof Date) {
    return createTimestampFromMillis(value.getTime())
  }
  if (typeof value === 'string') {
    const parsed = new Date(value)
    return createTimestampFromMillis(parsed.getTime())
  }
  return createTimestampFromMillis(value)
}

export const isTimestampLike = (value: unknown): value is TimestampObjectLike => {
  if (!value || typeof value !== 'object') return false
  const candidate = value as TimestampObjectLike
  return (
    typeof candidate.toDate === 'function' ||
    typeof candidate.toMillis === 'function' ||
    typeof candidate.seconds === 'number' ||
    typeof candidate._seconds === 'number'
  )
}

export const normalizeTimestampLike = (value: unknown): TimestampLike => {
  if (value instanceof Date) return createTimestampLike(value)
  if (typeof value === 'number') return createTimestampLike(value)
  if (typeof value === 'string') return createTimestampLike(value)
  if (isTimestampLike(value)) {
    if (typeof value.toMillis === 'function') {
      return createTimestampFromMillis(value.toMillis())
    }
    const seconds = value.seconds ?? value._seconds ?? 0
    const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0
    return createTimestampFromMillis(seconds * 1000 + Math.floor(nanoseconds / 1_000_000))
  }

  const parsed = new Date(value as any)
  return createTimestampFromMillis(parsed.getTime())
}
